/**
 * Third-party Sticky Add to Cart Integration
 * Opens the cart drawer when the sticky ATC button adds items to cart
 * Integrated with theme's cart-drawer system
 */

class StickyATCCartIntegration {
  constructor() {
    this.stickyButtonSelector = '.eascStickyAddToCartBtn';
    this.stickyButton = null;
    this.previousCartCount = null;
    this.init();
  }

  init() {
    this.waitForDependencies().then(() => {
      this.observeStickyButton();
      this.attachListener();
    }).catch(() => {});
  }

  waitForDependencies() {
    return new Promise((resolve) => {
      const checkDependencies = () => {
        const hasRoutes = window.routes && window.routes.cart_url;
        const hasCartDrawer = document.querySelector('cart-drawer');
        
        if (hasRoutes && hasCartDrawer) {
          resolve();
          return;
        }
        setTimeout(checkDependencies, 100);
      };
      checkDependencies();
    });
  }

  observeStickyButton() {
    document.addEventListener('click', (event) => {
      const clickedButton = event.target.closest(this.stickyButtonSelector);
      if (clickedButton) {
        this.handleStickyButtonClick();
      }
    }, true);

    const observer = new MutationObserver(() => {
      this.updateButtonReference();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.updateButtonReference();
  }

  updateButtonReference() {
    const button = document.querySelector(this.stickyButtonSelector);
    if (button && button !== this.stickyButton) {
      this.stickyButton = button;
      if (this.previousCartCount === null) {
        this.getCartCount().then(count => {
          this.previousCartCount = count;
        });
      }
    }
  }

  attachListener() {
    this.updateButtonReference();
  }

  async handleStickyButtonClick() {
    const countBeforeClick = this.previousCartCount;
    await this.delay(300);
    
    const maxAttempts = 20;
    const pollInterval = 250;

    for (let i = 0; i < maxAttempts; i += 1) {
      await this.delay(pollInterval);
      const currentCount = await this.getCartCount();

      if (currentCount !== null && currentCount > (countBeforeClick || 0)) {
        this.previousCartCount = currentCount;
        await this.refreshAndOpenCartDrawer();
        return;
      }
    }

    const finalCount = await this.getCartCount();
    if (finalCount !== null) {
      this.previousCartCount = finalCount;
    }
    
    await this.refreshAndOpenCartDrawer();
  }

  async refreshAndOpenCartDrawer() {
    try {
      await this.refreshCartDrawer();
      await this.delay(150);
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (error) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }
  }

  async refreshCartDrawer() {
    return new Promise((resolve, reject) => {
      if (!window.routes || !window.routes.cart_url) {
        reject(new Error('Routes not available'));
        return;
      }

      Promise.all([
        fetch(window.routes.cart_url + '?section_id=cart-drawer').then(r => r.text()),
        fetch(window.routes.cart_url + '?section_id=cart-icon-bubble').then(r => r.text())
      ])
        .then(([cartDrawerHtml, cartIconHtml]) => {
          const cartDrawerDoc = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
          const cartIconDoc = new DOMParser().parseFromString(cartIconHtml, 'text/html');

          const cartDrawerItemsSource = cartDrawerDoc.querySelector('cart-drawer-items') ||
            cartDrawerDoc.querySelector('.shopify-section cart-drawer-items') ||
            cartDrawerDoc.querySelector('#CartDrawer cart-drawer-items') ||
            cartDrawerDoc.querySelector('cart-drawer cart-drawer-items');
          const cartDrawerItemsTarget = document.querySelector('cart-drawer-items');

          if (cartDrawerItemsTarget && cartDrawerItemsSource) {
            const sourceCartItems = cartDrawerItemsSource.querySelector('#CartDrawer-CartItems');
            const sourceItemsTable = cartDrawerItemsSource.querySelector('.cart-items tbody');
            const sourceCartItemsCount = sourceItemsTable ? sourceItemsTable.querySelectorAll('tr.cart-item').length : 0;

            const clonedElement = cartDrawerItemsSource.cloneNode(true);
            cartDrawerItemsTarget.replaceWith(clonedElement);
            
            const newCartDrawerItems = document.querySelector('cart-drawer-items');
            const newCartItems = newCartDrawerItems ? newCartDrawerItems.querySelector('#CartDrawer-CartItems') : null;
            const newItemsTable = newCartDrawerItems ? newCartDrawerItems.querySelector('.cart-items tbody') : null;
            const newCartItemsCount = newItemsTable ? newItemsTable.querySelectorAll('tr.cart-item').length : 0;

            if (sourceCartItemsCount > 0 && newCartItemsCount === 0 && newCartDrawerItems) {
              const emptyState = newCartDrawerItems.querySelector('.drawer-inner-empty');
              if (emptyState) {
                emptyState.remove();
              }
              
              newCartDrawerItems.classList.remove('is-empty');
              
              const sourceCartItemsHTML = sourceCartItems ? sourceCartItems.innerHTML : null;
              if (sourceCartItemsHTML) {
                let targetCartItems = newCartDrawerItems.querySelector('#CartDrawer-CartItems');
                if (!targetCartItems) {
                  const form = newCartDrawerItems.querySelector('#CartDrawer-Form');
                  if (form) {
                    targetCartItems = document.createElement('div');
                    targetCartItems.id = 'CartDrawer-CartItems';
                    targetCartItems.className = 'drawer-contents js-contents';
                    form.appendChild(targetCartItems);
                  }
                }
                
                if (targetCartItems) {
                  targetCartItems.innerHTML = sourceCartItemsHTML;
                }
              }
            }
          }

          const footerSelectors = ['.cart-drawer__footer', '.drawer-footer'];
          for (let i = 0; i < footerSelectors.length; i++) {
            const selector = footerSelectors[i];
            const targetElement = document.querySelector(selector);
            const sourceElement = cartDrawerDoc.querySelector('#CartDrawer ' + selector) ||
              cartDrawerDoc.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement.cloneNode(true));
              break;
            }
          }

          const cartIconTarget = document.getElementById('cart-icon-bubble');
          const cartIconSource = cartIconDoc.querySelector('#cart-icon-bubble') ||
            cartIconDoc.querySelector('.shopify-section');
          if (cartIconTarget && cartIconSource) {
            cartIconTarget.innerHTML = cartIconSource.innerHTML;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          const updatedCartDrawerItems = document.querySelector('cart-drawer-items');
          const drawerInner = cartDrawer ? cartDrawer.querySelector('.drawer-inner') : null;
          
          if (cartDrawer) {
            const hasItems = updatedCartDrawerItems &&
              updatedCartDrawerItems.querySelector('.cart-items tbody tr.cart-item') !== null;
            
            const isEmpty = !hasItems;
            
            if (updatedCartDrawerItems) {
              updatedCartDrawerItems.classList.toggle('is-empty', isEmpty);
            }
            
            cartDrawer.classList.toggle('is-empty', isEmpty);
            
            if (drawerInner && !isEmpty) {
              const emptyState = drawerInner.querySelector('.drawer-inner-empty');
              if (emptyState) {
                emptyState.remove();
              }
            }
          }

          if (typeof AOS !== 'undefined') {
            AOS.init();
          }

          if (cartDrawer && typeof cartDrawer.ensureReviewsPopulated === 'function') {
            setTimeout(() => cartDrawer.ensureReviewsPopulated(), 300);
          }

          setTimeout(() => {
            resolve();
          }, 300);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count;
    } catch (error) {
      return null;
    }
  }

  delay(ms) {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyATCCartIntegration();
  });
} else {
  new StickyATCCartIntegration();
}
